import type {
  VoyageCharterInput,
  UpdateVoyageCharterByIdInput,
  DeleteOrderVoyageCharterRelationByIdInput,
  UpdateVoyageCharterOrderFreightByIdInput,
  UpdateVoyageCharterToContractByIdInput,
  DeleteVoyageCharterToContractByIdInput,
  DeleteVoyageCharterPortDatumByIdInput,
  CreatedOrExisting,
  CommitOrderVoyageCharterRelationInput,
  CommitVoyageCharterOrderFreightInput,
  CommitVoyageCharterToContractInput,
  CommitVoyageCharterPortDatumInput,
  CommitInput,
} from '../../graphql';
import type { ScenarioOrder } from '../../lib/planningData';

import { assertDefined, isDefined } from '../../lib/util';
import type { VoyageCharterDataForCommit } from '../ScenarioUtil';

export type CreateOrUpdateVoyageCharterArgs = {
  createVoyageCharter: (input: VoyageCharterInput) => CreatedOrExisting;
  updateVoyageCharter: (input: UpdateVoyageCharterByIdInput) => void;
  createOrderRel: (
    input: CommitOrderVoyageCharterRelationInput,
  ) => CreatedOrExisting;
  deleteOrderRel: (input: DeleteOrderVoyageCharterRelationByIdInput) => void;
  createVoyageCharterOrderFreight: (
    input: CommitVoyageCharterOrderFreightInput,
  ) => CreatedOrExisting;
  updateVoyageCharterOrderFreight: (
    input: UpdateVoyageCharterOrderFreightByIdInput,
  ) => void;
  createContractRel: (
    input: CommitVoyageCharterToContractInput,
  ) => CreatedOrExisting;
  updateContractRel: (input: UpdateVoyageCharterToContractByIdInput) => void;
  deleteContractRel: (input: DeleteVoyageCharterToContractByIdInput) => void;
  createPortDataRel: (
    input: CommitVoyageCharterPortDatumInput,
  ) => CreatedOrExisting;
  deletePortDataRel: (input: DeleteVoyageCharterPortDatumByIdInput) => void;
};

function hasChanges(
  potentialChanges: Record<string, unknown>,
  existing: Record<string, unknown>,
) {
  for (const key of Object.keys(potentialChanges)) {
    if (potentialChanges[key] !== existing[key]) {
      return true;
    }
  }
  return false;
}

export const createOrUpdateVoyageCharter = (
  // For now we assume this array contains all the orders we want to have associated
  // with this voyage charter so that we can keep port data and port calls in sync
  orders: ScenarioOrder[],
  organizationId: number,
  userId: number,
  vesselId: number | null,
  existingVoyageCharterId: number | null,
  spotShipownerOrgId: number | null,
  coaId: number | null,
  _createdPortCallIdsByOrderId: Map<number, CreatedOrExisting[]>,
  coasById: VoyageCharterDataForCommit['coasById'],
  voyageChartersById: VoyageCharterDataForCommit['voyageChartersById'],
  commitMutators: CreateOrUpdateVoyageCharterArgs,
  commitInputBefore: CommitInput,
): void => {
  const {
    createVoyageCharter,
    updateVoyageCharter,
    createOrderRel,
    deleteOrderRel,
    createVoyageCharterOrderFreight,
    updateVoyageCharterOrderFreight,
    createContractRel,
    updateContractRel,
    deleteContractRel,
    createPortDataRel,
    deletePortDataRel,
  } = commitMutators;

  const orderIds = orders.map((o) => o.id);

  // Fetch existing voyage charter and coa if available
  const existingVoyageCharter =
    existingVoyageCharterId !== null
      ? voyageChartersById.get(existingVoyageCharterId)
      : null;

  if (existingVoyageCharterId !== null) {
    assertDefined(existingVoyageCharter, 'Data for existing voyage charter');
  }

  const coa = coaId !== null ? coasById.get(coaId) : null;
  const coaAssortments =
    coa?.coaContractAssortmentsByCoaContractId.nodes.filter(isDefined) ?? [];

  if (coaId !== null && !coa) {
    assertDefined(coa, 'COA for voyage charter creation not available');
  }

  const existingOrderRelations =
    existingVoyageCharter?.orderVoyageCharterRelationsByVoyageCharterId?.nodes.filter(
      isDefined,
    ) ?? [];

  // Clean up any old voyage charter relations, currently relation cleanup
  // does not happen if order is not unassigned before moving it to another voyage
  // charter
  const orderRelationIdsToClean = orders
    .map((o) => o.outgoingVoyageCharterRelation)
    .filter(isDefined)
    .filter((rel) => rel.voyageCharterId !== existingVoyageCharterId)
    .map((orderRel) => orderRel.id);

  /** Some existing relations may have been already deleted when cleaning up a deleted order assignment (when order reassignment as delete + create) */
  const existingOrderVCRelsAfterClean = existingOrderRelations.filter(
    (rel) =>
      !orderRelationIdsToClean.includes(rel.id) &&
      !commitInputBefore.deleteOrderVoyageCharterRelations?.includes(rel.id),
  );

  orderRelationIdsToClean.forEach((orderRelId) =>
    deleteOrderRel({
      id: orderRelId,
    }),
  );

  const existingContractRel =
    existingVoyageCharter?.voyageCharterToContractsByVoyageCharterId.nodes[0] ??
    null;
  const existingVoyageCharterCoaId = existingContractRel?.coaId ?? null;

  const existingPortDataRels =
    existingVoyageCharter?.voyageCharterPortDataByVoyageCharterId?.nodes.filter(
      isDefined,
    ) ?? [];

  // If voyage was moved to another coa we need to update and remove relations to match new coa
  const movedToNewCoa =
    typeof existingVoyageCharterCoaId === 'number' &&
    coaId !== null &&
    existingVoyageCharterCoaId !== coaId;

  // If voyage was moved from coa to spot we need to clear any data inherited from that coa
  const movedFromCoaToSpot =
    typeof existingVoyageCharterCoaId === 'number' && coaId === null;

  const existingFreightsAfterClean = existingOrderVCRelsAfterClean
    .map((oRel) => {
      const freight =
        oRel.voyageCharterOrderFreightsByOrderVoyageCharterRelationId.nodes[0];

      return freight
        ? {
            ...freight,
            orderId: oRel.orderId,
          }
        : undefined;
    })
    .filter(isDefined);

  const shipownerOrgId = coaId
    ? (coa?.shipownerOrgId ?? null)
    : spotShipownerOrgId;

  let voyageCharterInput: VoyageCharterInput = {
    organisationId: organizationId,
    chartererOrgId:
      existingVoyageCharter?.chartererOrgId ||
      orders
        .map((order) => order.chartererOrganizationId)
        .filter((orgId) => orgId)
        // Are all elements in array the same?
        .reduce(
          (acc, cur, index) =>
            index === 0 ? cur : acc === cur ? acc : undefined,
          undefined,
        ) ||
      organizationId,
    vesselId,
    shipownerOrgId,
    isInquiry: false,
    isPreliminary: false,
  };

  if (movedFromCoaToSpot) {
    voyageCharterInput = {
      ...voyageCharterInput,
      demurrage: null,
      paymentTime: null,
      currency: null,
      paymentType: null,
      paymentTrigger: null,
    };
  }

  if (coa) {
    const coaVessel = vesselId
      ? coa.coaContractShipsByCoaContractId.nodes
          .filter(isDefined)
          .find((v) => v.shipId === vesselId)
      : null;

    if (vesselId !== null && !coaVessel) {
      throw new Error(`Vessel ${vesselId} is not present in COA ${coa.id}`);
    }

    orders.forEach((o) => {
      /** This check doesn't take amount restrictions into account */
      const coaAssortment = coaAssortments.find((ca) => {
        const assortmentMatches = ca.assortmentId === o.assortmentId;
        const assortmentGroupMatches =
          o.assortmentByAssortmentId?.assortmentGroup &&
          ca.assortmentGroupId === o.assortmentByAssortmentId.assortmentGroup;
        const isCatchAll =
          ca.assortmentId === null && ca.assortmentGroupId === null;

        return assortmentMatches || assortmentGroupMatches || isCatchAll;
      });

      if (!coaAssortment) {
        throw new Error(
          `Assortment ${o.assortmentByAssortmentId?.name} is not present in COA ${coa.id} for order ${o.id}`,
        );
      }
    });

    voyageCharterInput = {
      ...voyageCharterInput,
      demurrage: coaVessel?.demurrage ?? null,
      paymentTime: coa.paymentDays,
      currency: coa.currency,
      paymentType: coa.paymentType,
      paymentTrigger: coa.paymentTrigger,
    };
  }

  let createdVoyageCharterId: CreatedOrExisting;

  if (existingVoyageCharterId === null) {
    createdVoyageCharterId = createVoyageCharter(voyageCharterInput);
  } else {
    if (!existingVoyageCharter) {
      throw new Error(
        'Voyage charter missing when it should exist: ' +
          existingVoyageCharterId,
      );
    }

    if (hasChanges(voyageCharterInput, existingVoyageCharter)) {
      updateVoyageCharter({
        id: existingVoyageCharterId,
        voyageCharterPatch: voyageCharterInput,
      });
    }

    createdVoyageCharterId = { existing: existingVoyageCharterId };
  }

  const assortmentIdByCoaAssortment = new Map<number, number>(
    coaAssortments.map((a) => [a.id, a.assortmentId ?? 0]),
  );

  // Create order relations where one doesn't already exist
  const createOrderRelResponses = orderIds
    .filter(
      (orderId) =>
        !existingOrderVCRelsAfterClean.find((rel) => rel.orderId === orderId),
    )
    .map((orderId) => {
      const order = orders.find((o) => o.id === orderId);
      return [
        orderId,
        createOrderRel({
          orderId,
          voyageCharterId: createdVoyageCharterId,
          outgoing: true,
          coaRouteId:
            coa?.coaContractPricesByCoaContractId.nodes
              .filter(isDefined)
              .find(
                (price) =>
                  assortmentIdByCoaAssortment.get(price.coaAssortmentId) ===
                    order?.assortmentId &&
                  price.coaContractPortByRouteId?.originId ===
                    order?.loadPortId &&
                  price.coaContractPortByRouteId?.destinationId ===
                    order?.dischargePortId,
              )?.routeId ?? null,
        }),
      ] as const;
    });

  const portDataRelsToDelete = new Set<number>();

  if (movedFromCoaToSpot) {
    existingFreightsAfterClean.forEach((freight) =>
      updateVoyageCharterOrderFreight({
        id: freight.id,
        voyageCharterOrderFreightPatch: {
          freightAmount: null,
          freightUnit: null,
        },
      }),
    );

    existingPortDataRels.forEach((portDataRel) =>
      portDataRelsToDelete.add(portDataRel.id),
    );
  }

  if (existingContractRel !== null) {
    if (movedToNewCoa) {
      updateContractRel({
        id: existingContractRel.id,
        voyageCharterToContractPatch: {
          coaId: coaId ?? null,
        },
      });
    } else if (movedFromCoaToSpot) {
      deleteContractRel({
        id: existingContractRel.id,
      });
    }
  }

  if (coa) {
    // Create freights
    const getCoaFreightForOrderId = (
      orderId: number,
      orderVoyageCharterRelationId: CreatedOrExisting,
    ) => {
      const order = orders.find((o) => o.id === orderId);
      assertDefined(order, `expected to find order ${orderId} for coa freight`);

      const coaAssortment = coaAssortments.find(
        (a) => a.assortmentId === order.assortmentId,
      );

      const coaPrice = coa.coaContractPricesByCoaContractId.nodes
        .filter(isDefined)
        .find(
          (price) =>
            assortmentIdByCoaAssortment.get(price.coaAssortmentId) ===
              order.assortmentId &&
            price.coaContractPortByRouteId?.originId === order.loadPortId &&
            price.coaContractPortByRouteId?.destinationId ===
              order.dischargePortId,
        );

      return {
        orderVoyageCharterRelationId,
        freightAmount: coaPrice?.price ?? null,
        freightUnit: coaAssortment?.paymentUnit ?? null,
      };
    };

    createOrderRelResponses.forEach(([orderId, created]) => {
      const input: CommitVoyageCharterOrderFreightInput =
        getCoaFreightForOrderId(orderId, created);
      createVoyageCharterOrderFreight(input);
    });

    if (movedToNewCoa) {
      existingFreightsAfterClean.forEach((freight) => {
        const newFreight = getCoaFreightForOrderId(freight.orderId, {
          existing: freight.orderVoyageCharterRelationId,
        });

        updateVoyageCharterOrderFreight({
          id: freight.id,
          voyageCharterOrderFreightPatch: {
            freightAmount: newFreight.freightAmount,
            freightUnit: newFreight.freightUnit,
          },
        });
      });
    }

    // Create relation to coa if one does not exist yet
    if (existingContractRel === null) {
      createContractRel({
        voyageCharterId: createdVoyageCharterId,
        coaId,
        userId,
      });
    }

    // Create relations to any new port data
    const existingPortDataIds = existingPortDataRels.map(
      (portDataRel) => portDataRel.portDataId,
    );

    const portIds = new Set();
    orders.forEach((o) => {
      portIds.add(o.loadPortId);
      portIds.add(o.dischargePortId);
    });

    const coaPortDataIds = coa.coaContractPortDataByCoaContractId.nodes
      .filter(isDefined)
      .filter((coaPortDataRel) =>
        portIds.has(coaPortDataRel.portDatumByPortDataId?.portId),
      )
      .map((coaPortDataRel) => coaPortDataRel.portDataId);

    coaPortDataIds
      .filter((portDataId) => !existingPortDataIds.includes(portDataId))
      .forEach((portDataId) =>
        createPortDataRel({
          voyageCharterId: createdVoyageCharterId,
          portDataId,
        }),
      );

    existingPortDataRels.forEach((portDataRel) => {
      if (!coaPortDataIds.includes(portDataRel.portDataId)) {
        portDataRelsToDelete.add(portDataRel.id);
      }
    });
  }

  // Delete any leftover relations to port data from previous COA
  Array.from(portDataRelsToDelete).map((portDataRelId) =>
    deletePortDataRel({
      id: portDataRelId,
    }),
  );
};
